import React, { useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  CalendarDays,
  Check,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Edit3,
  MessageCircle,
  RefreshCw,
  Send,
  Sparkles,
  Trash2,
  UserRound,
  X,
} from "lucide-react";

import { getAccessToken } from "../../../../awsAuth";
import { useAuth } from "../../../../contexts/AuthContext";
import {
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import "./BookingPublic.scss";

const API_BASE =
  process.env.REACT_APP_API_URL ||
  "https://cvanto-api.onrender.com";

function pad(value) {
  return String(value).padStart(2, "0");
}

function toDateKey(date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )}`;
}

function addDays(date, count) {
  const next = new Date(date);
  next.setDate(next.getDate() + count);
  return next;
}

function startOfDay(date) {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
}

function parseDateKey(dateKey) {
  if (!dateKey) return null;

  const date = new Date(`${dateKey}T12:00:00`);

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date;
}

function slotToDate(dateKey, time) {
  if (!dateKey || !time) return null;

  const [year, month, day] = String(dateKey)
    .split("-")
    .map(Number);

  const [hours, minutes] = String(time)
    .split(":")
    .map(Number);

  if (
    !year ||
    !month ||
    !day ||
    !Number.isFinite(hours) ||
    !Number.isFinite(minutes)
  ) {
    return null;
  }

  const date = new Date(
    year,
    month - 1,
    day,
    hours,
    minutes,
    0,
    0
  );

  return Number.isNaN(date.getTime()) ? null : date;
}

function isSlotInFuture(dateKey, slot, minimumMinutes = 1) {
  const startDate = slotToDate(dateKey, slot?.start);

  if (!startDate) return false;

  const minimumDate = new Date(
    Date.now() + minimumMinutes * 60 * 1000
  );

  return startDate > minimumDate;
}

function formatWeekday(date) {
  return date
    .toLocaleDateString("uk-UA", {
      weekday: "short",
    })
    .replace(".", "");
}

function formatDayNumber(date) {
  return date.toLocaleDateString("uk-UA", {
    day: "2-digit",
  });
}

function formatMonthLabel(date) {
  return date.toLocaleDateString("uk-UA", {
    month: "long",
    year: "numeric",
  });
}

function formatFullDate(dateKey) {
  const date = parseDateKey(dateKey);

  if (!date) return "";

  return date.toLocaleDateString("uk-UA", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
}

function formatShortDate(dateKey) {
  const date = parseDateKey(dateKey);

  if (!date) return "";

  const today = startOfDay(new Date());
  const target = startOfDay(date);
  const difference = Math.round((target - today) / 86400000);

  if (difference === 0) return "Сьогодні";
  if (difference === 1) return "Завтра";

  return date.toLocaleDateString("uk-UA", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
}

function normalizeUaPhoneLocal(value) {
  let digits = String(value || "").replace(/\D/g, "");

  if (digits.startsWith("380")) {
    digits = digits.slice(3);
  } else if (digits.startsWith("0")) {
    digits = digits.slice(1);
  }

  return digits.slice(0, 9);
}

function toUaPhone(value) {
  const local = normalizeUaPhoneLocal(value);

  return local ? `+380${local}` : "";
}

function formatUaPhoneLocal(value) {
  const digits = normalizeUaPhoneLocal(value);

  const first = digits.slice(0, 2);
  const second = digits.slice(2, 5);
  const third = digits.slice(5, 7);
  const fourth = digits.slice(7, 9);

  return [first, second, third, fourth].filter(Boolean).join(" ");
}

function formatServiceMeta(service) {
  const parts = [];

  if (service?.duration) {
    parts.push(service.duration);
  }

  if (Number(service?.price || 0) > 0) {
    parts.push(`${service.price} ${service.currency || "ГРН"}`);
  }

  return parts.length ? parts.join(" · ") : "Деталі уточнюються";
}

function normalizeServices(services) {
  return (Array.isArray(services) ? services : []).map((item) => ({
    id: String(item.listingId || item.id || ""),
    title: item.title || "Послуга",
    duration: item.duration || "",
    price: item.price || "",
    currency: item.currency || "ГРН",
    description: item.description || "",
    photo: item.mainPhoto || item.photos?.[0] || "",
  }));
}

function statusLabel(status) {
  const labels = {
    pending: "Очікує",
    confirmed: "Підтверджено",
    declined: "Відхилено",
    cancelled: "Скасовано",
    completed: "Завершено",
    reschedule_proposed: "Запропоновано інший час",
    reschedule_requested: "Запит на перенесення",
  };

  return labels[status] || "Запис";
}

const ACTIVE_CLIENT_BOOKING_STATUSES = [
  "pending",
  "confirmed",
  "reschedule_requested",
  "reschedule_proposed",
];

function isActiveClientBookingStatus(status) {
  return ACTIVE_CLIENT_BOOKING_STATUSES.includes(status);
}

function clientBookingTitle(status) {
  const titles = {
    pending: "Заявку надіслано",
    confirmed: "Ви записані",
    reschedule_requested: "Запит на перенесення надіслано",
    reschedule_proposed: "Запропоновано інший час",
  };

  return titles[status] || "Ваш запис";
}

function clientBookingDescription(status) {
  const descriptions = {
    pending:
      "Спеціаліст ще не підтвердив заявку. Після відповіді ви отримаєте сповіщення.",
    confirmed:
      "Запис підтверджено. Цей час уже зарезервований для вас.",
    reschedule_requested:
      "Ви запропонували новий час. Очікуємо відповідь спеціаліста.",
    reschedule_proposed:
      "Спеціаліст запропонував інший час. Оберіть, чи підходить він вам.",
  };

  return descriptions[status] || "";
}

export default function BookingPublic({
  ownerId,
  services = [],
  initialService = null,
  ownerView = false,
}) {
  const { userId: currentUserId } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const focusedBookingId = searchParams.get("booking") || "";
  const normalizedServices = useMemo(
    () => normalizeServices(services),
    [services]
  );

  const hasServices = normalizedServices.length > 0;

  const [settings, setSettings] = useState(null);
  const [ownerBookings, setOwnerBookings] = useState([]);
  const [clientBookings, setClientBookings] = useState([]);
  const [clientBookingsLoading, setClientBookingsLoading] =
    useState(false);

  const [bookingActionLoading, setBookingActionLoading] =
    useState("");

  const [rescheduleBookingId, setRescheduleBookingId] =
    useState("");

  const [clientActionError, setClientActionError] =
    useState("");

  const [clientActionSuccess, setClientActionSuccess] =
    useState("");
  const [ownerSavingId, setOwnerSavingId] = useState("");
  const [ownerActionError, setOwnerActionError] = useState("");
  const [ownerActionSuccess, setOwnerActionSuccess] = useState("");
  const [ownerComments, setOwnerComments] = useState({});
  const [openedOwnerBookingId, setOpenedOwnerBookingId] = useState("");

  const [step, setStep] = useState(hasServices ? 1 : 2);
  const [selectedServiceId, setSelectedServiceId] = useState("");
  const [dateMode, setDateMode] = useState("nearest");

  const [selectedDate, setSelectedDate] = useState(
    toDateKey(new Date())
  );

  const [weekStart, setWeekStart] = useState(
    startOfDay(new Date())
  );

  const [slots, setSlots] = useState([]);
  const [nearestSlots, setNearestSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);

  const [clientName, setClientName] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [comment, setComment] = useState("");

  const [loading, setLoading] = useState(true);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  const selectedService = useMemo(() => {
    return (
      normalizedServices.find(
        (service) => service.id === selectedServiceId
      ) ||
      normalizedServices[0] ||
      null
    );
  }, [normalizedServices, selectedServiceId]);

  const weekDays = useMemo(() => {
    return Array.from({ length: 7 }).map((_, index) =>
      addDays(weekStart, index)
    );
  }, [weekStart]);

  const availableSlots = useMemo(() => {
    return (Array.isArray(slots) ? slots : []).filter(
      (slot) =>
        slot.available &&
        isSlotInFuture(selectedDate, slot)
    );
  }, [slots, selectedDate]);

  const activeClientBooking = useMemo(() => {
  if (ownerView || !ownerId) return null;

  const active = clientBookings.filter((booking) => {
    const sameOwner =
      String(booking.ownerId || "") === String(ownerId);

    return (
      sameOwner &&
      isActiveClientBookingStatus(booking.status)
    );
  });

  if (!active.length) return null;

  /*
   * Якщо сторінку відкрили зі сповіщення —
   * показуємо конкретний запис зі сповіщення.
   */
  if (focusedBookingId) {
    const focused = active.find(
      (booking) =>
        String(booking.bookingId || "") ===
        String(focusedBookingId)
    );

    if (focused) return focused;
  }

  /*
   * Якщо користувач обрав конкретну послугу —
   * шукаємо активний запис лише на цю послугу.
   *
   * Якщо запису на неї немає — повертаємо null,
   * щоб можна було створити новий запис.
   */
  if (selectedService?.id) {
    return (
      active.find(
        (booking) =>
          String(booking.serviceId || "") ===
          String(selectedService.id)
      ) || null
    );
  }

  /*
   * Fallback лише для профілів без каталогу послуг.
   */
  if (!hasServices) {
    return active[0] || null;
  }

  return null;
}, [
  clientBookings,
  ownerId,
  ownerView,
  selectedService?.id,
  focusedBookingId,
  hasServices,
]);

const isRescheduling =
  !!rescheduleBookingId &&
  String(activeClientBooking?.bookingId || "") ===
    String(rescheduleBookingId);

  useEffect(() => {
  const incomingId =
    initialService?.listingId ||
    initialService?.id ||
    initialService?.serviceId ||
    "";

  if (!incomingId) return;

  setSelectedServiceId(String(incomingId));
  setSelectedSlot(null);
  setDateMode("nearest");
  setStep(3);
}, [initialService]);

  useEffect(() => {
    if (!selectedServiceId && normalizedServices[0]?.id) {
      setSelectedServiceId(normalizedServices[0].id);
    }
  }, [normalizedServices, selectedServiceId]);

  useEffect(() => {
    if (!hasServices && step === 1) {
      setStep(2);
    }
  }, [hasServices, step]);

  useEffect(() => {
    loadSettings();
  }, [ownerId]);

  useEffect(() => {
  let alive = true;

  async function loadCurrentClient() {
    if (!currentUserId || ownerView) return;

    try {
      const token = await getAccessToken().catch(() => null);

      const response = await fetch(
        `${API_BASE}/api/account/${encodeURIComponent(currentUserId)}`,
        {
          headers: {
            ...(token
              ? {
                  Authorization: `Bearer ${token}`,
                }
              : {}),
          },
        }
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok || !alive) return;

      const accountData = data?.user || data || {};

      setClientName((current) => {
        if (current.trim()) return current;

        return (
          accountData.username ||
          accountData.userName ||
          accountData.name ||
          ""
        );
      });

      setClientPhone((current) => {
        if (current.trim()) return current;

        return normalizeUaPhoneLocal(
          accountData.phone ||
            accountData.phoneNumber ||
            accountData.phone_number ||
            ""
        );
      });
    } catch (clientError) {
      console.error(
        "[BookingPublic] current client account error:",
        clientError
      );
    }
  }

  loadCurrentClient();

  return () => {
    alive = false;
  };
}, [currentUserId, ownerView]);

  useEffect(() => {
    if (!ownerView) return;
    loadOwnerBookings();
  }, [ownerView, ownerId]);

  useEffect(() => {
  if (ownerView || !currentUserId) return;

  loadClientBookings();
}, [ownerView, currentUserId, ownerId]);

  useEffect(() => {
  if (!ownerView || !focusedBookingId) return;

  setOpenedOwnerBookingId(focusedBookingId);

  const timer = window.setTimeout(() => {
    const element = document.getElementById(
      `owner-booking-${focusedBookingId}`
    );

    element?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  }, 350);

  return () => {
    window.clearTimeout(timer);
  };
}, [ownerView, focusedBookingId, ownerBookings.length]);

  useEffect(() => {
    if (!settings?.enabled || ownerView) return;

    if (dateMode === "nearest") {
      findNearestSlots();
      return;
    }

    loadSlots(selectedDate);
  }, [
    settings?.enabled,
    ownerId,
    ownerView,
    dateMode,
    selectedDate,
  ]);

  async function loadSettings() {
    if (!ownerId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError("");

      const response = await fetch(
        `${API_BASE}/api/business/booking/settings/user/${encodeURIComponent(
  ownerId
)}`
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          data?.error || "Не вдалося завантажити онлайн-запис"
        );
      }

      setSettings(data?.settings || null);
    } catch (loadError) {
      console.error("[BookingPublic] settings error:", loadError);

      setError(
        loadError.message || "Не вдалося завантажити онлайн-запис"
      );
    } finally {
      setLoading(false);
    }
  }

  async function loadSlots(dateKey, silent = false) {
    if (!ownerId || !dateKey) return [];

    try {
      if (!silent) {
        setSlotsLoading(true);
      }

      const response = await fetch(
        `${API_BASE}/api/business/booking/slots/${encodeURIComponent(
  ownerId
)}?date=${encodeURIComponent(dateKey)}`
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          data?.error || "Не вдалося завантажити вільний час"
        );
      }

      const nextSlots = Array.isArray(data?.slots)
        ? data.slots
        : [];

      if (!silent) {
        setSlots(nextSlots);
      }

      return nextSlots;
    } catch (slotsError) {
      console.error("[BookingPublic] slots error:", slotsError);

      if (!silent) {
        setSlots([]);
      }

      return [];
    } finally {
      if (!silent) {
        setSlotsLoading(false);
      }
    }
  }

  async function findNearestSlots() {
    if (!settings?.enabled || !ownerId) return;

    try {
      setSlotsLoading(true);

      const today = startOfDay(new Date());

      const maxDays = Math.max(
        1,
        Number(settings.maxDaysAhead || 30)
      );

      const found = [];

      for (let index = 0; index < maxDays; index += 1) {
        const dateKey = toDateKey(addDays(today, index));
        const daySlots = await loadSlots(dateKey, true);

        const available = daySlots
          .filter(
            (slot) =>
              slot.available &&
              isSlotInFuture(dateKey, slot)
          )
          .map((slot) => ({
            ...slot,
            date: dateKey,
          }));

        found.push(...available);

        if (found.length >= 6) {
          break;
        }
      }

      setNearestSlots(found.slice(0, 6));
    } finally {
      setSlotsLoading(false);
    }
  }

  async function loadOwnerBookings() {
    if (!ownerView) return;

    try {
      const token = await getAccessToken();

      const response = await fetch(
        `${API_BASE}/api/business/booking/requests/me?role=owner`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        setOwnerBookings(
          Array.isArray(data?.items) ? data.items : []
        );
      }
    } catch (ownerError) {
      console.error(
        "[BookingPublic] owner bookings error:",
        ownerError
      );
    }
  }

  async function loadClientBookings() {
  if (ownerView || !currentUserId) return;

  try {
    setClientBookingsLoading(true);

    const token = await getAccessToken();

    const response = await fetch(
      `${API_BASE}/api/business/booking/requests/me?role=client`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(
        data?.error || "Не вдалося завантажити ваші записи"
      );
    }

    setClientBookings(
      Array.isArray(data?.items) ? data.items : []
    );
  } catch (loadError) {
    console.error(
      "[BookingPublic] client bookings error:",
      loadError
    );
  } finally {
    setClientBookingsLoading(false);
  }
}

  const selectService = (serviceId) => {
    setSelectedServiceId(serviceId);
    setSelectedSlot(null);
  };

  const continueFromService = () => {
  if (hasServices && !selectedService) return;

  setDateMode("nearest");
  setSelectedSlot(null);
  setStep(3);
};

  const chooseDateMode = (nextMode) => {
    setDateMode(nextMode);
    setSelectedSlot(null);

    if (nextMode === "nearest") {
      setStep(3);
      return;
    }

    setStep(2);
  };

  const chooseDate = (dateKey) => {
    setSelectedDate(dateKey);
    setSelectedSlot(null);
    setDateMode("date");
    setStep(3);
  };

  const chooseSlot = (slot, dateKey) => {
  setSelectedDate(dateKey);

  setSelectedSlot({
    ...slot,
    date: dateKey,
  });

  setStep(4);
  setError("");
  setClientActionError("");
};

const updateClientBooking = async (
  bookingId,
  payload,
  successMessage
) => {
  if (!bookingId) return null;

  try {
    setBookingActionLoading(bookingId);
    setClientActionError("");
    setClientActionSuccess("");

    const token = await getAccessToken();

    const response = await fetch(
      `${API_BASE}/api/business/booking/requests/${encodeURIComponent(
  bookingId
)}`,
      {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      }
    );

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(
        data?.error || "Не вдалося змінити запис"
      );
    }

    const updated = data?.booking;

    if (updated?.bookingId) {
      setClientBookings((current) =>
        current.map((booking) =>
          booking.bookingId === updated.bookingId
            ? updated
            : booking
        )
      );
    }

    setClientActionSuccess(successMessage || "Запис оновлено");

    window.setTimeout(() => {
      setClientActionSuccess("");
    }, 3500);

    return updated || null;
  } catch (actionError) {
    console.error(
      "[BookingPublic] client booking action error:",
      actionError
    );

    setClientActionError(
      actionError.message || "Не вдалося змінити запис"
    );

    return null;
  } finally {
    setBookingActionLoading("");
  }
};

const cancelClientBooking = async (booking) => {
  if (!booking?.bookingId) return;

  const confirmed = window.confirm(
    booking.status === "confirmed"
      ? "Скасувати підтверджений запис?"
      : "Скасувати заявку на запис?"
  );

  if (!confirmed) return;

  const updated = await updateClientBooking(
    booking.bookingId,
    {
      action: "client_cancel",
      comment: "",
    },
    "Запис скасовано"
  );

  if (updated) {
    setRescheduleBookingId("");
    setSelectedSlot(null);
    setStep(hasServices ? 1 : 2);
  }
};

const startClientReschedule = (booking) => {
  if (!booking?.bookingId) return;

  setRescheduleBookingId(booking.bookingId);
  setSelectedSlot(null);
  setSelectedDate(booking.date || toDateKey(new Date()));
  setWeekStart(
    parseDateKey(booking.date) || startOfDay(new Date())
  );
  setDateMode("nearest");
  setStep(2);
  setError("");
  setClientActionError("");
};

const stopClientReschedule = () => {
  setRescheduleBookingId("");
  setSelectedSlot(null);
  setDateMode("nearest");
  setStep(hasServices ? 1 : 2);
  setError("");
};

const acceptProposedSlot = async (booking, slot) => {
  if (!booking?.bookingId || !slot) return;

  await updateClientBooking(
    booking.bookingId,
    {
      action: "accept_proposal",
      chosenSlot: {
        date: slot.date,
        start: slot.start,
        end: slot.end,
      },
    },
    "Новий час підтверджено"
  );
};

const declineProposedSlot = async (booking) => {
  if (!booking?.bookingId) return;

  const confirmed = window.confirm(
    "Відмовитися від запропонованого часу?"
  );

  if (!confirmed) return;

  await updateClientBooking(
    booking.bookingId,
    {
      action: "decline_proposal",
    },
    "Запропонований час відхилено"
  );
};

  const submitBooking = async () => {
  if (!ownerId || !selectedSlot) return;

  if (rescheduleBookingId) {
    const updated = await updateClientBooking(
      rescheduleBookingId,
      {
        action: "request_reschedule",
        requestedSlot: {
          date: selectedSlot.date || selectedDate,
          start: selectedSlot.start,
          end: selectedSlot.end,
        },
        comment: comment.trim(),
      },
      "Запит на перенесення надіслано"
    );

    if (updated) {
      setRescheduleBookingId("");
      setSelectedSlot(null);
      setComment("");
      setStep(1);

      if (dateMode === "date") {
        loadSlots(selectedDate);
      } else {
        findNearestSlots();
      }
    }

    return;
  }

  if (!clientName.trim()) {
    setError("Вкажіть імʼя");
    return;
  }

  if (normalizeUaPhoneLocal(clientPhone).length !== 9) {
    setError("Вкажіть коректний номер телефону");
    return;
  }

  try {
    setSending(true);
    setError("");
    setSuccess("");

    const token = await getAccessToken();

    const response = await fetch(
      `${API_BASE}/api/business/booking/request/${encodeURIComponent(
  ownerId
)}`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          serviceId: selectedService?.id || "",
          serviceTitle:
            selectedService?.title || "Запис",
          date: selectedSlot.date || selectedDate,
          start: selectedSlot.start,
          end: selectedSlot.end,
          clientName: clientName.trim(),
          clientPhone: toUaPhone(clientPhone),
          comment: comment.trim(),
        }),
      }
    );

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      if (
        response.status === 409 &&
        data?.booking?.bookingId
      ) {
        setClientBookings((current) => {
          const exists = current.some(
            (booking) =>
              booking.bookingId ===
              data.booking.bookingId
          );

          return exists
            ? current
            : [data.booking, ...current];
        });
      }

      throw new Error(
        data?.error || "Не вдалося надіслати заявку"
      );
    }

    const createdBooking = data?.booking;

    if (createdBooking?.bookingId) {
      setClientBookings((current) => [
        createdBooking,
        ...current.filter(
          (booking) =>
            booking.bookingId !==
            createdBooking.bookingId
        ),
      ]);
    }

    const confirmed =
      createdBooking?.status === "confirmed";

    setSuccess(
      confirmed
        ? "Запис підтверджено"
        : "Заявку надіслано"
    );

    setStep(5);
    setComment("");

    if (dateMode === "date") {
      loadSlots(selectedDate);
    } else {
      findNearestSlots();
    }
  } catch (submitError) {
    console.error(
      "[BookingPublic] submit error:",
      submitError
    );

    setError(
      submitError.message ||
        "Не вдалося створити запис"
    );
  } finally {
    setSending(false);
  }
};

  const updateOwnerBooking = async (bookingId, action) => {
  if (!ownerView || !bookingId) return;

  try {
    setOwnerSavingId(bookingId);
    setOwnerActionError("");
    setOwnerActionSuccess("");

    const token = await getAccessToken();

    const response = await fetch(
      `${API_BASE}/api/business/booking/requests/${encodeURIComponent(
  bookingId
)}`,
      {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          action,
          comment: ownerComments[bookingId] || "",
        }),
      }
    );

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(
        data?.error || "Не вдалося оновити заявку"
      );
    }

    const updatedBooking = data?.booking;

    if (updatedBooking?.bookingId) {
      setOwnerBookings((current) =>
        current.map((booking) =>
          booking.bookingId === updatedBooking.bookingId
            ? updatedBooking
            : booking
        )
      );
    }

    setOwnerActionSuccess(
      action === "confirm"
        ? "Запис підтверджено"
        : "Заявку відхилено"
    );

    window.setTimeout(() => {
      setOwnerActionSuccess("");
    }, 3000);
  } catch (actionError) {
    console.error(
      "[BookingPublic] owner booking action error:",
      actionError
    );

    setOwnerActionError(
      actionError.message || "Не вдалося оновити заявку"
    );
  } finally {
    setOwnerSavingId("");
  }
};

const closeFocusedBooking = () => {
  setOpenedOwnerBookingId("");

  const nextParams = new URLSearchParams(searchParams);
  nextParams.delete("booking");

  setSearchParams(nextParams, {
    replace: true,
  });
};

  const resetFlow = () => {
    setStep(hasServices ? 1 : 2);
    setSelectedSlot(null);
    setDateMode("nearest");
    setSuccess("");
    setError("");
  };

  if (loading) {
    return (
      <div className="bpBookFlow">
        <div className="bpBookFlow__state">
          Завантаження онлайн-запису...
        </div>
      </div>
    );
  }

  if (ownerView) {
  const pendingOwnerBookings = ownerBookings.filter((booking) =>
    [
      "pending",
      "reschedule_requested",
      "reschedule_proposed",
    ].includes(String(booking?.status || ""))
  ).length;

  const confirmedOwnerBookings = ownerBookings.filter(
    (booking) =>
      String(booking?.status || "") === "confirmed"
  ).length;

  return (
    <div className="bpBookFlow bpBookFlow--owner">
      <section className="bpOwnerBookingWidget">
        <div className="bpOwnerBookingWidget__glow" />

        <div className="bpOwnerBookingWidget__top">
          <div className="bpOwnerBookingWidget__icon">
            <CalendarDays size={25} strokeWidth={2.25} />
          </div>

          <div className="bpOwnerBookingWidget__title">
            <span>ОНЛАЙН-ЗАПИС</span>

            <strong>Керування записами</strong>

            <p>
              Заявки клієнтів, підтверджені записи,
              перенесення та календар.
            </p>
          </div>

          <div className="bpOwnerBookingWidget__status">
            <span />
            Активний
          </div>
        </div>

        <div className="bpOwnerBookingWidget__stats">
          <div>
            <span className="is-orange">
              <Clock3 size={17} strokeWidth={2.3} />
            </span>

            <strong>{pendingOwnerBookings}</strong>
            <em>потребують відповіді</em>
          </div>

          <div>
            <span className="is-green">
              <Check size={17} strokeWidth={2.5} />
            </span>

            <strong>{confirmedOwnerBookings}</strong>
            <em>підтверджено</em>
          </div>

          <div>
            <span className="is-blue">
              <CalendarDays size={17} strokeWidth={2.3} />
            </span>

            <strong>{ownerBookings.length}</strong>
            <em>усього записів</em>
          </div>
        </div>

        <button
          type="button"
          className="bpOwnerAllBookingsButton"
          onClick={() => navigate("/business/bookings")}
        >
          <span>
            <CalendarDays size={19} strokeWidth={2.35} />

            <span>
              <strong>Відкрити журнал записів</strong>
              <em>
                Календар, заявки та історія
              </em>
            </span>
          </span>

          <ChevronRight size={20} strokeWidth={2.4} />
        </button>
      </section>
    </div>
  );
}

  if (!settings?.enabled) {
    return null;
  }

  if (
  !ownerView &&
  activeClientBooking &&
  !isRescheduling
) {
  const booking = activeClientBooking;

  const proposedSlots = Array.isArray(
    booking.proposedSlots
  )
    ? booking.proposedSlots
    : [];

  const isPending = booking.status === "pending";
  const isConfirmed = booking.status === "confirmed";
  const isRescheduleRequested =
    booking.status === "reschedule_requested";
  const isRescheduleProposed =
    booking.status === "reschedule_proposed";

  return (
    <div className="bpBookFlow">
      <header className="bpBookFlow__header">
        <div>
          <span className="bpBookFlow__eyebrow">
            Онлайн-запис
          </span>

          <h2>Ваш запис</h2>

          <p>
            Усі деталі та актуальний статус заявки.
          </p>
        </div>

        <div
          className={`bpClientBookingStatus is-${
            booking.status || "pending"
          }`}
        >
          {isConfirmed ? (
            <Check size={16} />
          ) : isRescheduleProposed ? (
            <RefreshCw size={16} />
          ) : (
            <Clock3 size={16} />
          )}

          {statusLabel(booking.status)}
        </div>
      </header>

      {clientActionError && (
        <div className="bpBookFlow__error">
          {clientActionError}
        </div>
      )}

      {clientActionSuccess && (
        <div className="bpOwnerActionSuccess">
          <Check size={18} />
          {clientActionSuccess}
        </div>
      )}

      <section
        className={`bpClientBookingCard is-${
          booking.status || "pending"
        }`}
      >
        <div className="bpClientBookingCard__icon">
          {isConfirmed ? (
            <Check size={30} strokeWidth={2.8} />
          ) : isRescheduleProposed ? (
            <RefreshCw size={28} />
          ) : (
            <Clock3 size={28} />
          )}
        </div>

        <span className="bpBookFlow__eyebrow">
          {isConfirmed
            ? "Підтверджено"
            : isPending
            ? "Очікує відповіді"
            : isRescheduleRequested
            ? "Перенесення"
            : "Новий час"}
        </span>

        <h3>
          {clientBookingTitle(booking.status)}
        </h3>

        <p>
          {clientBookingDescription(booking.status)}
        </p>

        <div className="bpClientBookingDetails">
          <div>
            <Sparkles size={19} />

            <span>
              <strong>
                {booking.serviceTitle ||
                  "Онлайн-запис"}
              </strong>

              <em>Обрана послуга</em>
            </span>
          </div>

          <div>
            <CalendarDays size={19} />

            <span>
              <strong>
                {formatFullDate(booking.date)}
              </strong>

              <em>
                {booking.start}–{booking.end}
              </em>
            </span>
          </div>
        </div>

        {isRescheduleRequested &&
          booking.requestedSlot && (
            <div className="bpClientRequestedSlot">
              <RefreshCw size={18} />

              <span>
                <strong>
                  Ви запропонували новий час
                </strong>

                <em>
                  {formatFullDate(
                    booking.requestedSlot.date
                  )}{" "}
                  · {booking.requestedSlot.start}–
                  {booking.requestedSlot.end}
                </em>
              </span>
            </div>
          )}

        {booking.ownerComment && (
          <div className="bpClientBookingComment">
            <MessageCircle size={18} />

            <span>
              <strong>
                Повідомлення спеціаліста
              </strong>

              <em>{booking.ownerComment}</em>
            </span>
          </div>
        )}

        {isRescheduleProposed &&
          proposedSlots.length > 0 && (
            <div className="bpClientProposals">
              <div className="bpClientProposals__head">
                <strong>
                  Оберіть запропонований час
                </strong>

                <span>
                  Натисніть, якщо він вам підходить
                </span>
              </div>

              <div className="bpClientProposals__list">
                {proposedSlots.map((slot, index) => (
                  <button
                    key={`${slot.date}-${slot.start}-${index}`}
                    type="button"
                    disabled={
                      bookingActionLoading ===
                      booking.bookingId
                    }
                    onClick={() =>
                      acceptProposedSlot(
                        booking,
                        slot
                      )
                    }
                  >
                    <span>
                      <strong>
                        {formatFullDate(slot.date)}
                      </strong>

                      <em>
                        {slot.start}–{slot.end}
                      </em>
                    </span>

                    <Check size={18} />
                  </button>
                ))}
              </div>

              <button
                type="button"
                className="bpClientProposalDecline"
                disabled={
                  bookingActionLoading ===
                  booking.bookingId
                }
                onClick={() =>
                  declineProposedSlot(booking)
                }
              >
                Не підходить
              </button>
            </div>
          )}

        {!isRescheduleProposed && (
          <div className="bpClientBookingActions">
            <button
              type="button"
              className="is-reschedule"
              disabled={
                bookingActionLoading ===
                booking.bookingId
              }
              onClick={() =>
                startClientReschedule(booking)
              }
            >
              <RefreshCw size={18} />

              {isPending
                ? "Змінити час"
                : "Перенести запис"}
            </button>

            <button
              type="button"
              className="is-cancel"
              disabled={
                bookingActionLoading ===
                booking.bookingId
              }
              onClick={() =>
                cancelClientBooking(booking)
              }
            >
              <Trash2 size={18} />

              {bookingActionLoading ===
              booking.bookingId
                ? "Зберігаємо..."
                : "Скасувати"}
            </button>
          </div>
        )}
      </section>
    </div>
  );
}

  return (
    <div className="bpBookFlow">
      <section className="bpBookIntro">
  <div className="bpBookIntro__glow" />

  <div className="bpBookIntro__top">
    <div className="bpBookIntro__icon">
      {isRescheduling ? (
        <RefreshCw size={25} strokeWidth={2.3} />
      ) : (
        <CalendarDays size={25} strokeWidth={2.3} />
      )}
    </div>

    <div className="bpBookIntro__content">
      <span className="bpBookFlow__eyebrow">
        {isRescheduling
          ? "ПЕРЕНЕСЕННЯ ЗАПИСУ"
          : "ОНЛАЙН-ЗАПИС"}
      </span>

      <h2>
        {isRescheduling
          ? "Оберіть нову дату і час"
          : "Запишіться онлайн"}
      </h2>

      <p>
        {isRescheduling
          ? "Оберіть інший вільний час. Поточний запис діятиме, доки спеціаліст не підтвердить перенесення."
          : "Оберіть послугу, дату та час. Заповнення заявки займе приблизно одну хвилину."}
      </p>
    </div>

    <div className="bpBookFlow__availability">
      <span />
      Доступно
    </div>
  </div>

  {!isRescheduling && (
    <div className="bpBookIntro__trust">
      <span>
        <Clock3 size={15} strokeWidth={2.3} />
        Швидкий запис
      </span>

      <span>
        <Check size={15} strokeWidth={2.5} />
        Підтвердження спеціалістом
      </span>
    </div>
  )}
</section>

      {error && (
        <div className="bpBookFlow__error">
          {error}
        </div>
      )}

      {step > 1 && hasServices && selectedService && step < 5 && (
        <div className="bpBookCollapsedChoice">
          <div className="bpBookCollapsedChoice__icon">
            {selectedService.photo ? (
              <img src={selectedService.photo} alt="" />
            ) : (
              <Sparkles size={19} />
            )}
          </div>

          <div>
            <strong>{selectedService.title}</strong>
            <span>
              {formatServiceMeta(selectedService)}
            </span>
          </div>

          <button
            type="button"
            onClick={() => {
              setSelectedSlot(null);
              setStep(1);
            }}
          >
            <Edit3 size={16} />
            Змінити
          </button>
        </div>
      )}

      {step > 3 && selectedSlot && step < 5 && (
        <div className="bpBookCollapsedChoice">
          <div className="bpBookCollapsedChoice__icon">
            <CalendarDays size={19} />
          </div>

          <div>
            <strong>
              {formatFullDate(
                selectedSlot.date || selectedDate
              )}
            </strong>

            <span>
              {selectedSlot.start}–{selectedSlot.end}
            </span>
          </div>

          <button
            type="button"
            onClick={() => setStep(2)}
          >
            <Edit3 size={16} />
            Змінити
          </button>
        </div>
      )}

        {step === 1 && hasServices && !isRescheduling && (
        <section className="bpBookPanel">
          <div className="bpBookPanel__heading">
            <span>1</span>

            <div>
              <strong>Оберіть послугу</strong>
              <p>
                Спочатку оберіть, на що хочете записатися.
              </p>
            </div>
          </div>

          <div className="bpBookServiceList">
            {normalizedServices.map((service) => {
              const active =
                selectedService?.id === service.id;

              return (
                <button
                  key={service.id}
                  type="button"
                  className={active ? "is-active" : ""}
                  onClick={() => selectService(service.id)}
                >
                  <span className="bpBookServiceList__image">
                    {service.photo ? (
                      <img src={service.photo} alt="" />
                    ) : (
                      <Sparkles size={20} />
                    )}
                  </span>

                  <span className="bpBookServiceList__content">
                    <strong>{service.title}</strong>
                    <em>{formatServiceMeta(service)}</em>
                  </span>

                  <span className="bpBookServiceList__check">
                    {active && (
                      <Check size={15} strokeWidth={3} />
                    )}
                  </span>
                </button>
              );
            })}
          </div>

          <button
            type="button"
            className="bpBookPrimaryButton"
            onClick={continueFromService}
            disabled={!selectedService}
          >
            Продовжити
            <ChevronRight size={18} />
          </button>
        </section>
      )}

      {step === 2 && (
  <section className="bpBookPanel">
    <div className="bpBookPanel__heading">
      <span>
        <CalendarDays size={17} />
      </span>

      <div>
        <strong>Оберіть день</strong>
        <p>Натисніть на зручну дату.</p>
      </div>
    </div>

    <div className="bpBookCalendar">
      <div className="bpBookCalendar__month">
        <button
          type="button"
          aria-label="Попередній тиждень"
          onClick={() =>
            setWeekStart((previous) =>
              addDays(previous, -7)
            )
          }
        >
          <ChevronLeft size={18} />
        </button>

        <strong>{formatMonthLabel(weekStart)}</strong>

        <button
          type="button"
          aria-label="Наступний тиждень"
          onClick={() =>
            setWeekStart((previous) =>
              addDays(previous, 7)
            )
          }
        >
          <ChevronRight size={18} />
        </button>
      </div>

      <div className="bpBookCalendar__week">
        {weekDays.map((date) => {
          const key = toDateKey(date);
          const active = key === selectedDate;

          const isPast =
            startOfDay(date) <
            startOfDay(new Date());

          return (
            <button
              key={key}
              type="button"
              disabled={isPast}
              className={active ? "is-active" : ""}
              onClick={() => chooseDate(key)}
            >
              <span>{formatWeekday(date)}</span>
              <strong>{formatDayNumber(date)}</strong>
            </button>
          );
        })}
      </div>
    </div>

    <button
      type="button"
      className="bpBookSecondaryButton"
      onClick={() => {
        setDateMode("nearest");
        setStep(3);
      }}
    >
      <ChevronLeft size={17} />
      Повернутися до найближчого часу
    </button>
  </section>
)}

      {step === 3 && (
  <section className="bpBookPanel bpBookPanel--simpleTime">
    <div className="bpBookPanel__heading">
      <span>
        <Clock3 size={17} />
      </span>

      <div>
        <strong>
          {dateMode === "nearest"
            ? "Найближчий вільний час"
            : formatFullDate(selectedDate)}
        </strong>

        <p>
          Просто натисніть на час, який вам підходить.
        </p>
      </div>
    </div>

    {slotsLoading ? (
      <div className="bpBookFlow__state">
        Шукаємо вільний час...
      </div>
    ) : dateMode === "nearest" ? (
      nearestSlots.length ? (
        <div className="bpBookSimpleSlots">
          {nearestSlots.map((slot) => (
            <button
              key={`${slot.date}-${slot.start}`}
              type="button"
              onClick={() =>
                chooseSlot(slot, slot.date)
              }
            >
              <span>
                <strong>
                  {formatShortDate(slot.date)}
                </strong>

                <em>
                  {formatFullDate(slot.date)}
                </em>
              </span>

              <b>{slot.start}</b>

              <ChevronRight size={18} />
            </button>
          ))}
        </div>
      ) : (
        <div className="bpBookFlow__state">
          <strong>Вільного часу поки немає</strong>
          <span>
            Спробуйте обрати інший день.
          </span>
        </div>
      )
    ) : (
      <>
        <div className="bpBookSelectedDate">
          <CalendarDays size={18} />

          <span>{formatFullDate(selectedDate)}</span>

          <button
            type="button"
            onClick={() => setStep(2)}
          >
            Змінити
          </button>
        </div>

        {availableSlots.length ? (
          <div className="bpBookTimeGrid">
            {availableSlots.map((slot) => (
              <button
                key={`${selectedDate}-${slot.start}`}
                type="button"
                onClick={() =>
                  chooseSlot(slot, selectedDate)
                }
              >
                <strong>{slot.start}</strong>
                <span>до {slot.end}</span>
              </button>
            ))}
          </div>
        ) : (
          <div className="bpBookFlow__state">
            <strong>На цей день місць немає</strong>
            <span>Оберіть іншу дату.</span>
          </div>
        )}
      </>
    )}

    <button
      type="button"
      className="bpBookCalendarButton"
      onClick={() => {
        setDateMode("date");
        setSelectedSlot(null);
        setStep(2);
      }}
    >
      <CalendarDays size={18} />
      Обрати інший день
      <ChevronRight size={18} />
    </button>

    {hasServices && !isRescheduling && (
      <button
        type="button"
        className="bpBookSecondaryButton"
        onClick={() => {
          setSelectedSlot(null);
          setDateMode("nearest");
          setStep(1);
        }}
      >
        <ChevronLeft size={17} />
        Змінити послугу
      </button>
    )}

    {isRescheduling && (
      <button
        type="button"
        className="bpBookSecondaryButton"
        onClick={stopClientReschedule}
      >
        <X size={17} />
        Не переносити запис
      </button>
    )}
  </section>
)}

            {step === 4 && selectedSlot && (
        <section className="bpBookReview">
          <div className="bpBookPanel__heading">
            <span>
              <Check size={17} strokeWidth={2.8} />
            </span>

            <div>
              <strong>
                {isRescheduling
                  ? "Перевірте новий час"
                  : "Перевірте запис"}
              </strong>

              <p>
                {isRescheduling
                  ? "Якщо все правильно — надішліть запит."
                  : "Ваші дані вже заповнені автоматично."}
              </p>
            </div>
          </div>

          <div className="bpBookReview__summary">
            <div>
              <Sparkles size={18} />

              <span>
                <strong>
                  {selectedService?.title || "Запис"}
                </strong>

                <em>
                  {selectedService
                    ? formatServiceMeta(selectedService)
                    : "Онлайн-запис"}
                </em>
              </span>
            </div>

            <div>
              <CalendarDays size={18} />

              <span>
                <strong>
                  {formatFullDate(
                    selectedSlot.date || selectedDate
                  )}
                </strong>

                <em>
                  {selectedSlot.start}–{selectedSlot.end}
                </em>
              </span>
            </div>
          </div>

          <div className="bpBookReview__notice">
            <Clock3 size={18} />

            <span>
              {isRescheduling
                ? "Поточний запис залишиться чинним, доки спеціаліст не підтвердить новий час."
                : settings.manualConfirm
                ? "Спеціаліст підтвердить запис або запропонує інший час."
                : "Запис буде підтверджено автоматично."}
            </span>
          </div>

          {!isRescheduling && (
            <div className="bpBookReview__fields">
              <label>
                <span>Ваші дані</span>

                <div>
                  <UserRound size={18} />

                  <input
                    value={clientName}
                    onChange={(event) =>
                      setClientName(event.target.value)
                    }
                    placeholder="Ваше імʼя"
                    aria-label="Ваше імʼя"
                  />
                </div>
              </label>

              <label>
                <span>Номер для зв’язку</span>

                <div>
                  <span className="bpBookReview__phoneIcon">
                    +380
                  </span>

                  <input
                    type="tel"
                    inputMode="numeric"
                    autoComplete="tel"
                    value={formatUaPhoneLocal(clientPhone)}
                    onChange={(event) => {
                      setClientPhone(
                        normalizeUaPhoneLocal(
                          event.target.value
                        )
                      );
                    }}
                    placeholder="67 301 11 73"
                    maxLength={12}
                  />
                </div>
              </label>

              <label>
                <span>Коментар — необовʼязково</span>

                <textarea
                  value={comment}
                  onChange={(event) =>
                    setComment(event.target.value)
                  }
                  placeholder="Побажання або важливі деталі"
                />
              </label>
            </div>
          )}

          {isRescheduling && (
            <div className="bpBookRescheduleNotice">
              <RefreshCw size={18} />

              <span>
                <strong>Запит на перенесення</strong>

                <em>
                  Ви надсилаєте спеціалісту нову дату й
                  час. Старий запис не скасується, доки
                  спеціаліст не підтвердить перенесення.
                </em>
              </span>
            </div>
          )}

          <button
            type="button"
            className="bpBookPrimaryButton"
            onClick={submitBooking}
            disabled={
            sending ||
            (
              !!rescheduleBookingId &&
              bookingActionLoading === rescheduleBookingId
            )
          }
          >
            {sending ||
          (
            !!rescheduleBookingId &&
            bookingActionLoading === rescheduleBookingId
          ) ? (
            "Надсилаємо..."
          ) : (
              <>
                {isRescheduling ? (
                  <RefreshCw size={18} />
                ) : (
                  <Send size={18} />
                )}

                {isRescheduling
                  ? "Запросити перенесення"
                  : "Надіслати заявку"}
              </>
            )}
          </button>

          <button
            type="button"
            className="bpBookSecondaryButton"
            onClick={() => {
              if (isRescheduling) {
                stopClientReschedule();
                return;
              }

              setStep(3);
            }}
          >
            <ChevronLeft size={17} />

            {isRescheduling
              ? "Не переносити"
              : "Назад"}
          </button>
        </section>
      )}

      {step === 5 && (
        <section className="bpBookResult">
          <div className="bpBookResult__icon">
            <Check size={32} strokeWidth={3} />
          </div>

          <span className="bpBookFlow__eyebrow">
            Готово
          </span>

          <h3>{success || "Заявку надіслано"}</h3>

          <p>
            {settings.manualConfirm
              ? "Очікуйте підтвердження спеціаліста. Після відповіді ви отримаєте сповіщення."
              : "Ваш запис уже підтверджено. Деталі доступні у сповіщеннях."}
          </p>

          <div className="bpBookResult__summary">
            <strong>
              {selectedService?.title || "Онлайн-запис"}
            </strong>

            <span>
              {formatFullDate(
                selectedSlot?.date || selectedDate
              )}{" "}
              · {selectedSlot?.start}–{selectedSlot?.end}
            </span>
          </div>

          <button
            type="button"
            className="bpBookSecondaryButton"
            onClick={resetFlow}
          >
            Створити ще один запис
          </button>
        </section>
      )}
    </div>
  );
}